import './FoodDetailModal.css'; // Use modal styles
import './FoodItem.css'; // Custom styles for non-modal layout

import FoodImage from './FoodImage';
import NutriScoreDisplay from './NutriScoreDisplay';

const FoodItem = ({ foodData }) => {
  const {
    name,
    description,
    imageUrls,
    nutritional_score,
    approximate_calories,
    price,
    is_vegetarian,
    is_vegan,
    nutritional_summary,
    history,
    ingredients,
    showAllergens = true,
    // Allergen data
    milk,
    cereals_containing_gluten,
    eggs,
    peanuts,
    nuts,
    soybeans,
    fish,
    crustaceans,
    molluscs,
    sesame_seeds,
    celery,
    mustard,
    sulphur_dioxide_and_sulphites,
    lupin,
  } = foodData;

  // Get dietary badges with just icons (from FoodDetailModal)
  const getDietaryBadges = () => {
    const badges = [];
    if (is_vegetarian === true) badges.push({ icon: '🥬', label: 'Vegetarian' });
    if (is_vegan === true) badges.push({ icon: '🌱', label: 'Vegan' });
    return badges;
  };

  // Format allergens breakdown for display (from FoodDetailModal)
  const formatAllergensBreakdown = () => {
    const keyMap = {
      milk: { name: 'Milk', emoji: '🥛' },
      cereals_containing_gluten: { name: 'Gluten', emoji: '🌾' },
      eggs: { name: 'Eggs', emoji: '🥚' },
      peanuts: { name: 'Peanuts', emoji: '🥜' },
      nuts: { name: 'Tree Nuts', emoji: '🌰' },
      soybeans: { name: 'Soybeans', emoji: '🌱' },
      fish: { name: 'Fish', emoji: '🐟' },
      crustaceans: { name: 'Crustaceans', emoji: '🦐' },
      molluscs: { name: 'Molluscs', emoji: '🦪' },
      sesame_seeds: { name: 'Sesame Seeds', emoji: '⚫' },
      celery: { name: 'Celery', emoji: '🌿' },
      mustard: { name: 'Mustard', emoji: '🌭' },
      lupin: { name: 'Lupin', emoji: '🌾' },
      sulphur_dioxide_and_sulphites: { name: 'Sulphites', emoji: '🍷' },
    };

    const statusMap = {
      high: { icon: '⚠️', color: '#ff4d4f', displayText: 'High allergen risk' },
    };

    const formatValue = (key, value) => {
      if (value === 'high') {
        const status = statusMap[value];
        const allergenInfo = keyMap[key];
        return {
          key: `${allergenInfo.emoji} ${allergenInfo.name}`,
          value: value,
          icon: status.icon,
          color: status.color,
          displayValue: status.displayText,
          isBlurred: false,
        };
      }

      return null;
    };

    const allergenKeys = [
      'milk',
      'cereals_containing_gluten',
      'eggs',
      'peanuts',
      'nuts',
      'soybeans',
      'fish',
      'crustaceans',
      'molluscs',
      'sesame_seeds',
      'celery',
      'mustard',
      'lupin',
      'sulphur_dioxide_and_sulphites',
    ];

    const allergenData = {
      milk,
      cereals_containing_gluten,
      eggs,
      peanuts,
      nuts,
      soybeans,
      fish,
      crustaceans,
      molluscs,
      sesame_seeds,
      celery,
      mustard,
      sulphur_dioxide_and_sulphites,
      lupin,
    };

    return allergenKeys.map(key => formatValue(key, allergenData[key])).filter(allergen => allergen !== null);
  };

  const badges = getDietaryBadges();
  const allergenBreakdown = formatAllergensBreakdown();

  return (
    <div className="food-item-container">
      <div className="food-item-content">
        <div className="food-modal-image-section">
          {imageUrls && imageUrls.length > 0 ? (
            <FoodImage imageUrls={imageUrls} altText={name} />
          ) : (
            <div className="food-image-placeholder-modal">
              <span className="placeholder-icon-modal">🍝</span>
              <small>No image available</small>
            </div>
          )}
        </div>

        <div className="food-modal-content-section">
          <div className="food-title-row">
            <h2 className="food-title-airbnb">{name}</h2>
            {price && <span className="food-price-modal">{price}€</span>}
          </div>

          <p className="food-location-airbnb">Menu item details</p>

          <div className="food-stats-row">
            <div className="nutri-score-section-modal">
              <NutriScoreDisplay score={nutritional_score} size="normal" />
            </div>

            <div className="stats-divider">•</div>

            <div className="calories-airbnb">
              <span className="calories-value">🔥 {approximate_calories} calories</span>
            </div>

            {badges.length > 0 && (
              <>
                <div className="stats-divider">•</div>
                <div className="dietary-icons-airbnb">
                  {badges.map((badge, index) => (
                    <span key={index} className="dietary-icon-hover" title={badge.label}>
                      {badge.icon}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {nutritional_summary && (
            <div className="nutrition-summary-airbnb">
              <p>{nutritional_summary}</p>
            </div>
          )}

          {description && (
            <div className="description-section-airbnb">
              <h3 className="section-title-airbnb">Description of dish</h3>
              <p className="description-text-airbnb">{description}</p>
            </div>
          )}

          {showAllergens && (
            <div className="nutrient-breakdown-airbnb">
              <div className="breakdown-header">
                <h3 className="section-title-airbnb">Detailed Allergen Breakdown</h3>
                <span className="allergen-breakdown-note">
                  Always make sure to double check with restaurant before ordering. We can only give high/low
                  probability of allergen risk ⚠️.
                </span>
              </div>
              <div className="nutrient-grid-airbnb">
                {allergenBreakdown.map((allergen, index) => (
                  <div key={index} className="nutrient-item-airbnb">
                    <div className="allergen-item-content">
                      <span className="nutrient-name-airbnb">{allergen.key}</span>
                      <div className="nutrient-level-airbnb">
                        <span className="nutrient-icon-airbnb" style={{ color: allergen.color }}>
                          {allergen.icon}
                        </span>
                        <span className="nutrient-text-airbnb">{allergen.displayValue}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {history && (
            <div className="dish-history-section">
              <h3 className="section-title-airbnb">About This Dish</h3>
              <p className="dish-history-text">{history}</p>
            </div>
          )}

          {ingredients && (
            <div className="traditional-ingredients-section">
              <h3 className="section-title-airbnb">Traditional Ingredients</h3>
              {Array.isArray(ingredients) ? (
                <ul className="traditional-ingredients-list">
                  {ingredients.map((ingredient, index) => (
                    <li key={index} className="ingredient-item">
                      <strong>{ingredient.name}:</strong> {ingredient.description}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="traditional-ingredients-text">{ingredients}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoodItem;
